/**
 * ChatSyncService — mirrors the user's chat list/messages to Firestore so
 * they migrate across devices.
 *
 * Storage layout:
 *   users/{userId}/chats/{chatId}                  — chat metadata
 *   users/{userId}/chats/{chatId}/messages/{msgId} — one doc per message
 *
 * The Cloud Run streaming service already writes session/SDK metadata to
 * users/{uid}/chats/{cid}; this service coexists by writing distinct fields
 * (title, lastMessageAt, messageCount, archived) and a brand-new messages
 * sub-collection.
 *
 * Localhost is intentionally sandboxed — localStorage-only. Cross-device
 * sync is a re-stack.com property.
 */

import {
  collection,
  doc,
  query,
  orderBy,
  onSnapshot,
  setDoc,
  deleteDoc,
  writeBatch,
  getDocs,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { isLocalEnvironment } from '../../utils';
import {
  TRANSCRIPT_MODE_LOCKER_SCOPED,
  TRANSCRIPT_MODE_ORDINARY,
  TRANSCRIPT_MODE_PENDING,
  LOCKER_SCOPED_SAFE_CHAT_TITLE,
  TranscriptPolicyError,
  ownerReadableTranscriptAllowed,
  transcriptModeForChat,
} from './chatTranscriptPolicy';

export const isChatSyncEnabled = () => !isLocalEnvironment();

const CHATS_PATH = 'chats';
const MESSAGES_SUBPATH = 'messages';

// Truncate fat research reports to keep individual message docs under
// Firestore's 1MB cap. Same threshold the localStorage path uses.
const RESEARCH_TEXT_CAP = 8000;

// Conservative cap on serialized message size — well under Firestore's 1MB
// hard limit so we have headroom for the timestamp/metadata overhead.
const MAX_MESSAGE_DOC_SIZE = 900_000;

// An assistant message's components ARE its body: there is no `text` field on
// the reply, so a doc written without components renders as a bare duration
// chip and nothing else. When a long turn overruns the budget, these are the
// components a reload cannot rebuild, so they are the last to go. Everything
// else (reasoning traces, tool status, progress cards) narrates work that has
// already finished. The localStorage path keeps the same idea in
// presentationHooks.js as ESSENTIAL_COMPONENT_TYPES.
const DURABLE_COMPONENT_TYPES = new Set([
  'text',
  'research',
  'citations',
  'report',
  'createSlides',
  'editSlides',
  'attachments',
  'steered_user_message',
  'clarification_response',
  'turnError',
  'retry',
  'timer',
]);

const serializedSize = (value) => {
  try {
    return JSON.stringify(value).length;
  } catch {
    return Number.POSITIVE_INFINITY;
  }
};

const userChatsCol = (userId) =>
  collection(db, 'users', userId, CHATS_PATH);
const chatDocRef = (userId, chatId) =>
  doc(db, 'users', userId, CHATS_PATH, chatId);
const chatMessagesCol = (userId, chatId) =>
  collection(db, 'users', userId, CHATS_PATH, chatId, MESSAGES_SUBPATH);
const messageDocRef = (userId, chatId, messageId) =>
  doc(db, 'users', userId, CHATS_PATH, chatId, MESSAGES_SUBPATH, messageId);

// ---------------------------------------------------------------------------
// Serialization helpers
// ---------------------------------------------------------------------------

const parseTs = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : NaN;
  }
  if (value instanceof Date) return value.getTime();
  if (value && typeof value.toMillis === 'function') return value.toMillis();
  return NaN;
};

const toTimestamp = (value, fallback = Date.now()) => {
  const ms = parseTs(value);
  return Timestamp.fromMillis(Number.isFinite(ms) ? ms : fallback);
};

const fromTimestamp = (value) => {
  if (!value) return null;
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value?.toDate === 'function') return value.toDate().toISOString();
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return new Date(value).toISOString();
  return null;
};

// Drop welcome_cards (UI bootstrap only) and truncate huge research reports.
const prepareComponent = (component) => {
  if (!component || component.type === 'welcome_cards') return null;
  if (component.type === 'research' && component.data) {
    const data = { ...component.data };
    if (typeof data.fullReport === 'string' && data.fullReport.length > RESEARCH_TEXT_CAP) {
      data.fullReport = data.fullReport.slice(0, RESEARCH_TEXT_CAP) + '\n…[truncated]';
    }
    if (typeof data.report === 'string' && data.report.length > RESEARCH_TEXT_CAP) {
      data.report = data.report.slice(0, RESEARCH_TEXT_CAP) + '\n…[truncated]';
    }
    return { ...component, data };
  }
  return component;
};

// Strip undefined values recursively — Firestore rejects them.
const stripUndefined = (input) => {
  if (input === null || input === undefined) return null;
  if (Array.isArray(input)) {
    return input.map(stripUndefined).filter((v) => v !== undefined);
  }
  if (input instanceof Timestamp || input instanceof Date) return input;
  if (typeof input !== 'object') return input;
  const out = {};
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined) continue;
    out[key] = stripUndefined(value);
  }
  return out;
};

const serializeMessage = (msg) => {
  const components = (msg.components || [])
    .map(prepareComponent)
    .filter((c) => c !== null);

  let payload = stripUndefined({
    id: msg.id,
    sender: msg.sender || 'user',
    timestamp: toTimestamp(msg.timestamp),
    // Author app ('powerpoint' | 'excel' | 'word'): drives the cross-app
    // attribution lines on every pane rendering the shared transcript.
    app: msg.app ?? null,
    // Local-write revision (ms clock). Merges pick the higher rev, so a
    // fresher remote copy replaces a stale local one and vice versa.
    rev: typeof msg.rev === 'number' ? msg.rev : null,
    text: msg.text ?? null,
    metadata: msg.metadata ?? null,
    components: components.length > 0 ? components : null,
    attachments: msg.attachments ?? null,
    // Render-affecting top-level flags so follower panes style bubbles the
    // same way the author pane does (live spinner, error/cancelled states).
    isStreaming: typeof msg.isStreaming === 'boolean' ? msg.isStreaming : null,
    isError: msg.isError === true ? true : null,
    hasError: msg.hasError === true ? true : null,
    isCancelled: msg.isCancelled === true ? true : null,
    errorCode: msg.errorCode ?? null,
    requestDuration: typeof msg.requestDuration === 'number' ? msg.requestDuration : null
  });

  // Size cap. Every write here is a merge onto the message doc, and this doc
  // is the only durable copy of the reply, so shedding must never mean
  // writing an empty body: dropping all components used to overwrite content
  // that earlier, smaller flushes of the same turn had already stored, which
  // is what left a long turn rendering as a duration chip over empty space.
  // Shed the rebuildable components first, then keep the newest of what is
  // left while it fits, so the answer at the end of the turn survives.
  if (serializedSize(payload) > MAX_MESSAGE_DOC_SIZE && components.length > 0) {
    const durable = components.filter((c) => DURABLE_COMPONENT_TYPES.has(c?.type));
    let kept = durable;
    if (serializedSize({ ...payload, components: kept }) > MAX_MESSAGE_DOC_SIZE) {
      let remaining = MAX_MESSAGE_DOC_SIZE - serializedSize({ ...payload, components: [] });
      kept = [];
      for (let i = durable.length - 1; i >= 0; i -= 1) {
        const size = serializedSize(durable[i]) + 1;
        if (size > remaining) break;
        remaining -= size;
        kept.unshift(durable[i]);
      }
    }
    console.warn(
      '[ChatSync] Message over the document budget, shedding components:',
      msg.id,
      `kept ${kept.length} of ${components.length}`
    );
    payload = stripUndefined({
      ...payload,
      // Nothing fits at all (one component larger than the whole budget):
      // leave the field out so the merge keeps whatever is already stored
      // rather than replacing real content with an empty body.
      components: kept.length > 0 ? kept : undefined,
      _truncated: true,
      _truncatedReason: 'doc-size-exceeded'
    });
  }

  return payload;
};

const deserializeMessage = (data) => ({
  id: data.id,
  sender: data.sender,
  timestamp: fromTimestamp(data.timestamp) || new Date().toISOString(),
  app: data.app ?? undefined,
  rev: typeof data.rev === 'number' ? data.rev : undefined,
  text: data.text ?? undefined,
  metadata: data.metadata ?? undefined,
  components: Array.isArray(data.components) ? data.components : [],
  attachments: data.attachments ?? undefined,
  isStreaming: typeof data.isStreaming === 'boolean' ? data.isStreaming : undefined,
  isError: data.isError === true ? true : undefined,
  hasError: data.hasError === true ? true : undefined,
  isCancelled: data.isCancelled === true ? true : undefined,
  errorCode: data.errorCode ?? undefined,
  requestDuration: typeof data.requestDuration === 'number' ? data.requestDuration : undefined
});

const serializeChatMeta = (chat) => {
  const transcriptMode = transcriptModeForChat(chat);
  return stripUndefined({
    title: transcriptMode === TRANSCRIPT_MODE_LOCKER_SCOPED
      ? LOCKER_SCOPED_SAFE_CHAT_TITLE
      : (chat.name || chat.title || 'New chat'),
    createdAt: toTimestamp(chat.createdAt),
    lastMessageAt: toTimestamp(chat.updatedAt || chat.lastMessageAt || chat.createdAt),
    messageCount: transcriptMode === TRANSCRIPT_MODE_LOCKER_SCOPED
      && Number.isInteger(chat.messageCount)
      ? chat.messageCount
      : (chat.messages || []).length,
    archived: chat.archived === true,
    // Cross-pane flags: origin labels the chat by the app it started in;
    // conductorMinted marks connector plumbing chats so EVERY pane's history
    // filter hides them; pinned keeps sidebar pinning consistent per user.
    origin: chat.origin ?? null,
    conductorMinted: chat.conductorMinted === true,
    pinned: chat.pinned === true,
    transcriptMode
  });
};

const deserializeChat = (chatId, data, messages) => {
  const transcriptMode = data.transcriptMode ?? undefined;
  const title = transcriptMode === TRANSCRIPT_MODE_LOCKER_SCOPED
    ? LOCKER_SCOPED_SAFE_CHAT_TITLE
    : (data.title || 'New chat');
  return {
  id: chatId,
  name: title,
  title,
  createdAt: fromTimestamp(data.createdAt) || new Date(0).toISOString(),
  updatedAt: fromTimestamp(data.lastMessageAt) || new Date(0).toISOString(),
  archived: data.archived === true,
  origin: data.origin ?? undefined,
  conductorMinted: data.conductorMinted === true,
  pinned: data.pinned === true,
  transcriptMode,
  messageCount: Number.isInteger(data.messageCount) ? data.messageCount : 0,
  messages: messages || []
  };
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Subscribe to the chat list (metadata only). Receivers can pull each
 * chat's messages on demand via fetchChatMessages.
 *
 * @returns Unsubscribe function.
 */
export const subscribeChatList = (userId, callback) => {
  const q = query(userChatsCol(userId), orderBy('lastMessageAt', 'desc'));
  return onSnapshot(
    q,
    (snap) => {
      const chats = snap.docs.map((d) => deserializeChat(d.id, d.data(), null));
      callback(chats);
    },
    (err) => {
      console.warn('[ChatSync] chat list subscription error:', err.message);
    }
  );
};

/**
 * Fetch all messages for a single chat, oldest first.
 */
export const fetchChatMessages = async (userId, chatId, chat = null) => {
  if (!ownerReadableTranscriptAllowed(chat)) return [];
  const snap = await getDocs(query(chatMessagesCol(userId, chatId), orderBy('timestamp', 'asc')));
  return snap.docs.map((d) => deserializeMessage(d.data()));
};

/**
 * Live per-chat message mirror: every pane viewing chat C subscribes here and
 * renders the merged stream. The working pane's throttled sync loop rewrites
 * its streaming message each tick, so followers get near-live text for free.
 *
 * @returns Unsubscribe function.
 */
export const subscribeChatMessages = (userId, chatId, callback, chat = null) => {
  if (!ownerReadableTranscriptAllowed(chat)) return () => {};
  const q = query(chatMessagesCol(userId, chatId), orderBy('timestamp', 'asc'));
  return onSnapshot(
    q,
    (snap) => {
      const messages = snap.docs.map((d) => deserializeMessage(d.data()));
      callback(messages);
    },
    (err) => {
      console.warn('[ChatSync] chat messages subscription error:', chatId, err.message);
    }
  );
};

/**
 * Direct single-message write, bypassing the throttled upsert loop. Used for
 * transcript-level peer status lines ("Excel working...") whose later updates
 * would otherwise be skipped by the watermark (their timestamp never changes).
 */
export const upsertChatMessage = async (userId, chatId, message, chat = null) => {
  if (!isChatSyncEnabled()) return;
  if (!ownerReadableTranscriptAllowed(chat)) return false;
  try {
    await setDoc(messageDocRef(userId, chatId, message.id), serializeMessage(message), { merge: true });
    return true;
  } catch (err) {
    console.warn('[ChatSync] upsertChatMessage failed:', chatId, message?.id, err.message);
    return false;
  }
};

/**
 * Idempotent upsert: writes the caller-selected message copies plus (when
 * warranted) the chat metadata doc. Message SELECTION lives with the caller
 * (selectMessagesToPublish in transcriptMerge.js, driven by a per-message
 * publish cursor), so this function only performs the writes.
 *
 * @param {Array} messagesToWrite - message copies to publish
 * @param {Object} [opts]
 * @param {boolean} [opts.skipMetaWrite] - skip the chat metadata doc write
 *   unless a SETTLED message published (fresh lastMessageAt for list
 *   ordering). Callers gate it on a local meta fingerprint. Deliberately NOT
 *   written on live streaming ticks: each meta write re-fires every pane's
 *   chat-LIST snapshot, and live content already travels through the
 *   per-chat message mirror.
 * @returns {boolean} whether a settled (non-streaming) message was published
 */
export const upsertChat = async (userId, chat, messagesToWrite = [], opts = {}) => {
  const transcriptMode = transcriptModeForChat(chat);
  if (transcriptMode === TRANSCRIPT_MODE_PENDING) return false;
  if (
    transcriptMode === TRANSCRIPT_MODE_LOCKER_SCOPED
    && messagesToWrite.length > 0
  ) {
    throw new TranscriptPolicyError(
      'scoped_transcript_firestore_write_blocked',
      'Scope-bound transcript messages cannot be written to Firestore.'
    );
  }

  const messages = chat.messages || [];
  // Empty chats never sync, not even metadata. Connector plumbing chats and
  // untouched "New chat" stubs would otherwise ghost into every pane's list.
  if (
    messages.length === 0
    && !(transcriptMode === TRANSCRIPT_MODE_LOCKER_SCOPED && chat.messageCount > 0)
  ) return false;

  // Firestore allows up to 500 ops per batch; chunk for safety.
  const CHUNK = 400;
  for (let i = 0; transcriptMode === TRANSCRIPT_MODE_ORDINARY && i < messagesToWrite.length; i += CHUNK) {
    const batch = writeBatch(db);
    for (const m of messagesToWrite.slice(i, i + CHUNK)) {
      batch.set(messageDocRef(userId, chat.id, m.id), serializeMessage(m), { merge: true });
    }
    await batch.commit();
  }

  const wroteSettled = messagesToWrite.some((m) => !m.metadata?.isStreaming);

  if (!opts.skipMetaWrite || wroteSettled) {
    await setDoc(chatDocRef(userId, chat.id), serializeChatMeta(chat), { merge: true });
  }

  return wroteSettled;
};

/**
 * Delete a chat and every message under it. Best-effort — failures are
 * logged but don't propagate (local cache is the user-visible truth).
 */
export const deleteChatRemote = async (userId, chatId, chat = null) => {
  const transcriptMode = transcriptModeForChat(chat);
  if (transcriptMode === TRANSCRIPT_MODE_PENDING) return;
  try {
    if (transcriptMode === TRANSCRIPT_MODE_ORDINARY) {
      const messagesSnap = await getDocs(chatMessagesCol(userId, chatId));
      const docs = messagesSnap.docs;
      for (let i = 0; i < docs.length; i += 400) {
        const batch = writeBatch(db);
        for (const d of docs.slice(i, i + 400)) batch.delete(d.ref);
        await batch.commit();
      }
    }
    await deleteDoc(chatDocRef(userId, chatId));
  } catch (err) {
    console.warn('[ChatSync] Failed to delete chat from Firestore:', chatId, err.message);
  }
};
